import React from "react";
import { Helmet } from "react-helmet-async";
import { SITE_URL, DEFAULT_OG_IMAGE } from "@/utils/seo";

/**
 * PageSEO — drop this inside any page component to set per-page meta tags.
 *
 * Props:
 *   title         {string}  Full <title> text (e.g. "Disney World Stroller Rentals | Enchanted Gear Rentals")
 *   description   {string}  <meta name="description"> content
 *   canonical     {string}  Absolute canonical URL (without trailing slash)
 *   image         {string}  Optional OG image URL — falls back to site default
 *   type          {string}  og:type — "website" (default) or "article"
 *   structuredData {object} Optional JSON-LD object rendered as <script type="application/ld+json">
 *   noIndex       {boolean} Set true to add noindex (private/utility pages)
 */
export const PageSEO = ({
  title,
  description,
  canonical,
  image = DEFAULT_OG_IMAGE,
  type = "website",
  structuredData,
  noIndex = false,
}) => {
  const canonicalUrl = canonical || SITE_URL;

  return (
    <Helmet>
      <title>{title}</title>
      {description && <meta name="description" content={description} />}
      <link rel="canonical" href={canonicalUrl} />
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={title} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:url" content={canonicalUrl} />
      {image && <meta property="og:image" content={image} />}
      <meta property="og:site_name" content="Enchanted Gear Rentals" />

      {/* Twitter / X */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      {description && <meta name="twitter:description" content={description} />}
      {image && <meta name="twitter:image" content={image} />}

      {/* Structured data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};
